import { Mic, Shield, Lock, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SmartDownloadButton } from './SmartDownloadButton';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { buildPath } from '../routing';
import { normalizeLanguage } from '../i18n/utils';
import { useEffect, useState } from 'react';
import { ScrollReveal } from './ScrollReveal';

export function Hero() {
  const { t, i18n } = useTranslation();
  const currentLang = normalizeLanguage(i18n.language);
  const downloadPagePath = buildPath(currentLang, 'download');
  const pricingPath = buildPath(currentLang, 'pricing');
  const buyNowPath = `${pricingPath}#plans`;
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <section id="hero" className="bg-gradient-to-br from-teal-50 via-white to-teal-50">
      <div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20 lg:py-32"
        style={isMobile ? { paddingTop: '140px' } : undefined}
      >
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <ScrollReveal>
              <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full">
                <Shield className="size-4" />
                <span className="text-sm font-medium">{t('hero.badge')}</span>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={1}>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                {t('hero.title')}
              </h1>
            </ScrollReveal>
            
            <ScrollReveal delay={2}>
              <p className="text-2xl lg:text-3xl text-gray-700">
                {t('hero.subtitle')}
              </p>
            </ScrollReveal>
            
            <p className="text-lg text-gray-600 leading-relaxed">
              {t('hero.descriptionIntro')}{' '}
              <span className="font-semibold text-gray-900">
                {t('hero.descriptionStrong')}
              </span>{' '}
              {t('hero.descriptionOutro')}
            </p>
            
            {/* Smart Download Section */}
            <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ width: '260px', maxWidth: '100%' }}>
                <SmartDownloadButton
                  variant="primary"
                  className="w-full"
                  buttonStyle={{ height: '56px' }}
                />
                <a
                  href={downloadPagePath}
                  className="mt-4 text-sm text-gray-500 hover:text-teal-700 transition-colors flex items-center gap-2 w-full"
                >
                  <ChevronRight className="size-4" />
                  {t('hero.allVersionsLink')}
                </a>
              </div>
              <a
                href={buyNowPath}
                className="px-8 py-4 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-teal-600 border-2 border-teal-600"
                style={{ width: '260px', maxWidth: '100%', height: '56px' }}
              >
                {t('hero.buyNow')}
              </a>
            </div>
            
            <div className="flex flex-wrap gap-6 pt-4">
              <div className="flex items-center gap-2 text-gray-600">
                <Lock className="size-5 text-teal-600" />
                <span className="text-sm">{t('hero.featurePrivacy')}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Mic className="size-5 text-teal-600" />
                <span className="text-sm">{t('hero.featureTranscription')}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Shield className="size-5 text-teal-600" />
                <span className="text-sm">{t('hero.featureAnonymization')}</span>
              </div>
            </div>
          </div>
          
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
              <ImageWithFallback 
                src="figma:asset/716cc227e2a6931ea1911feb2a6571ea523ebd7c.png"
                alt="Noteon - Nowa sesja"
                className="w-full h-auto"
                showErrorPlaceholder={false}
                fallbackHeight="30px"
              />
            </div>
            
            <div
              className="absolute -bottom-6 bg-white rounded-xl shadow-lg p-6 border border-gray-200"
              style={{ left: isMobile ? '16px' : '-24px' }}
            >
              <div className="flex items-center gap-3">
                <div className="bg-teal-100 rounded-full p-3">
                  <Mic className="size-6 text-teal-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{t('hero.recordingBadge')}</p>
                  <p className="text-sm text-gray-600">{t('hero.recordingSubtext')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
